import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import toast from 'react-hot-toast';
import {
  ArrowRightIcon,
  BuildingOfficeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PhotoIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';

// ============================================
// Component: AgentReturnDetailPage
// Purpose: View details of a single return request
// ============================================

const STATUS_CONFIG = {
  requested: { label: 'בקשה חדשה', color: 'blue', icon: ClockIcon },
  pending_approval: { label: 'ממתין לאישור', color: 'yellow', icon: ClockIcon },
  approved: { label: 'אושר', color: 'green', icon: CheckCircleIcon },
  rejected: { label: 'נדחה', color: 'red', icon: XCircleIcon },
  label_sent: { label: 'תווית נשלחה', color: 'purple', icon: null },
  in_transit: { label: 'בדרך', color: 'indigo', icon: null },
  received: { label: 'התקבל', color: 'teal', icon: CheckCircleIcon },
  inspecting: { label: 'בבדיקה', color: 'orange', icon: null },
  completed: { label: 'הושלם', color: 'green', icon: CheckCircleIcon },
  cancelled: { label: 'בוטל', color: 'gray', icon: XCircleIcon }
};

const RETURN_TYPE_LABELS = {
  defective: 'מוצר פגום',
  wrong_item: 'מוצר שגוי',
  not_as_described: 'לא כמתואר',
  damaged_in_shipping: 'נזק במשלוח',
  changed_mind: 'לא נמכר / מלאי עודף',
  warranty_claim: 'תביעת אחריות',
  other: 'אחר'
};

const AgentReturnDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [returnData, setReturnData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReturnDetails();
  }, [id]);

  const fetchReturnDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/field-agent/returns/${id}`);
      setReturnData(response.data.return);
    } catch (error) {
      console.error('Error fetching return details:', error);
      toast.error('שגיאה בטעינת פרטי ההחזרה');
      navigate('/agent/returns');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { label: status, color: 'gray' };
    const colorClasses = {
      blue: 'bg-blue-100 text-blue-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      purple: 'bg-purple-100 text-purple-800',
      indigo: 'bg-indigo-100 text-indigo-800',
      teal: 'bg-teal-100 text-teal-800',
      orange: 'bg-orange-100 text-orange-800',
      gray: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-full ${colorClasses[config.color]}`}>
        {config.icon && <config.icon className="h-4 w-4" />}
        {config.label}
      </span>
    );
  };

  const getTotalQuantity = () => {
    return returnData?.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
  };

  const getTotalAmount = () => {
    // Priority: store_credit_amount (approved) > total_refund_amount > sum of items
    if (parseFloat(returnData?.store_credit_amount || 0) > 0) {
      return parseFloat(returnData.store_credit_amount);
    }
    if (parseFloat(returnData?.total_refund_amount || 0) > 0) {
      return parseFloat(returnData.total_refund_amount);
    }
    // Calculate from items as fallback
    return returnData?.items?.reduce((sum, item) => sum + parseFloat(item.refund_amount || 0), 0) || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!returnData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <header className="bg-white shadow-sm p-4 border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/agent/returns')}
            className="text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            <ArrowRightIcon className="h-5 w-5" />
            חזרה לרשימה
          </button>
          <h1 className="text-xl font-bold text-gray-900">פרטי החזרה</h1>
          <div className="w-24"></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 pb-8">
        {/* RMA Number & Status Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-sm text-gray-500 mb-1">מספר RMA</div>
              <div className="text-2xl font-mono font-bold text-primary-600">
                {returnData.rma_number}
              </div>
            </div>
            <div>
              {getStatusBadge(returnData.status)}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-4 text-sm text-gray-600">
            <div>
              <span className="font-medium">תאריך יצירה:</span> {formatDate(returnData.created_at)}
            </div>
            {returnData.completed_at && (
              <div>
                <span className="font-medium">תאריך השלמה:</span> {formatDate(returnData.completed_at)}
              </div>
            )}
          </div>
        </div>

        {/* Company Info */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
            פרטי לקוח
          </h2>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <BuildingOfficeIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <div className="font-semibold text-gray-900">{returnData.company?.name}</div>
              <div className="text-sm text-gray-500">ח.פ: {returnData.company?.business_number}</div>
            </div>
          </div>
        </div>

        {/* Return Details */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">פרטי ההחזרה</h2>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-600">סוג ההחזרה:</span>
              <span className="font-medium">{RETURN_TYPE_LABELS[returnData.return_type] || returnData.return_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">סיבת ההחזרה:</span>
              <span className="font-medium text-left">{returnData.return_reason}</span>
            </div>
            {returnData.order && (
              <div className="flex justify-between">
                <span className="text-gray-600">מספר הזמנה:</span>
                <span className="font-mono text-primary-600">{returnData.order.order_number}</span>
              </div>
            )}
          </div>
        </div>

        {/* Items */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ShoppingBagIcon className="h-5 w-5 text-gray-400" />
            פריטים להחזרה ({getTotalQuantity()} יח')
          </h2>
          <div className="space-y-4">
            {returnData.items?.map((item, index) => (
              <div key={item.id || index} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                <img
                  src={item.product?.images?.[0] || '/placeholder-product.png'}
                  alt={item.product?.name}
                  className="w-20 h-20 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{item.product?.name}</div>
                  <div className="text-sm text-gray-500 mt-1">מק"ט: {item.product?.sku}</div>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm">
                    <div>
                      <span className="text-gray-500">כמות:</span>{' '}
                      <span className="font-medium">{item.quantity}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">מחיר יחידה:</span>{' '}
                      <span className="font-medium">{formatCurrency(item.unit_price)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">סה"כ:</span>{' '}
                      <span className="font-medium text-green-600">{formatCurrency(item.refund_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total Summary */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-gray-900">סה"כ זיכוי (כולל מע"מ):</span>
              <span className="text-2xl font-bold text-green-600">{formatCurrency(getTotalAmount())}</span>
            </div>
          </div>
        </div>

        {/* Photos */}
        {returnData.photos && returnData.photos.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <PhotoIcon className="h-5 w-5 text-gray-400" />
              תמונות
            </h2>
            <div className="grid grid-cols-3 gap-4">
              {returnData.photos.map((photo, index) => (
                <img
                  key={photo.id || index}
                  src={photo.photo_url}
                  alt={`תמונה ${index + 1}`}
                  className="w-full aspect-square object-cover rounded-lg"
                />
              ))}
            </div>
          </div>
        )}

        {/* Timeline */}
        {returnData.timeline && returnData.timeline.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">היסטוריה</h2>
            <div className="space-y-4">
              {returnData.timeline.map((event, index) => (
                <div key={event.id || index} className="flex gap-4">
                  <div className="w-2 h-2 mt-2 rounded-full bg-primary-500"></div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{event.event_description}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      {formatDate(event.created_at)}
                      {event.actor_name && ` • ${event.actor_name}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AgentReturnDetailPage;
